// FLOW-11 (task 5.1, G18) — o leitor de cURL compartilhado: a tela de tools (`ToolsCrud.vue`) e o
// editor de flow (`curlParaPassoHttp.js`) partem da mesma leitura. O de antes era um punhado de
// regex sobre o texto inteiro, que não entendia a quebra de linha com `\`, aspas dentro de aspas
// nem o `$'…'` que o "Copiar como cURL (bash)" do Chrome usa quando o corpo tem quebra de linha ou
// `!`; e achava a "URL" dentro de um header `Referer`.
//
// Aqui o texto é separado em palavras como o bash separa (`palavras`), e as opções são lidas como o
// curl as lê. O resultado é uma estrutura neutra, sem decidir o que vira o quê: quem decide é o
// adaptador de cada tela. O formato do cmd do Windows (`^` no fim da linha, `^"` nas aspas) é
// recusado, para a pessoa colar no formato bash.

// Erros: `cmd` (formato do cmd do Windows), `aspas` (aspas que não fecham), `nao_e_curl` (a primeira
// palavra não é `curl`) e `sem_url`.
export const ERROS_DO_CURL = ['cmd', 'aspas', 'nao_e_curl', 'sem_url'];

class ErroDoCurl extends Error {
  constructor(codigo) {
    super(codigo);
    this.codigo = codigo;
  }
}

// O `$'…'` do bash: as sequências de escape do C. O `\u` e o `\U` levam até 4 e 8 dígitos.
const ESCAPES_SIMPLES = {
  a: '\x07',
  b: '\b',
  e: '\x1b',
  E: '\x1b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t',
  v: '\v',
  '\\': '\\',
  "'": "'",
  '"': '"',
  '?': '?',
};
const HEX = /[0-9a-fA-F]/;
const OCTAL = /[0-7]/;

function lerAnsiC(texto, inicio) {
  let valor = '';
  let i = inicio;
  while (i < texto.length) {
    const c = texto[i];
    if (c === "'") return { valor, fim: i + 1 };
    if (c !== '\\') {
      valor += c;
      i += 1;
    } else {
      const e = texto[i + 1];
      if (e === undefined) break;
      if (e in ESCAPES_SIMPLES) {
        valor += ESCAPES_SIMPLES[e];
        i += 2;
      } else if (e === 'x' || e === 'u' || e === 'U') {
        const maximo = { x: 2, u: 4, U: 8 }[e];
        let j = i + 2;
        while (j < i + 2 + maximo && HEX.test(texto[j] || '')) j += 1;
        if (j === i + 2) {
          valor += `\\${e}`;
        } else {
          valor += String.fromCodePoint(parseInt(texto.slice(i + 2, j), 16));
        }
        i = j;
      } else if (OCTAL.test(e)) {
        let j = i + 1;
        while (j < i + 4 && OCTAL.test(texto[j] || '')) j += 1;
        valor += String.fromCharCode(parseInt(texto.slice(i + 1, j), 8));
        i = j;
      } else if (e === 'c' && texto[i + 2]) {
        valor += String.fromCharCode(texto.charCodeAt(i + 2) % 32);
        i += 3;
      } else {
        valor += `\\${e}`;
        i += 2;
      }
    }
  }
  throw new ErroDoCurl('aspas');
}

// As aspas duplas do bash: o `\` só escapa `"`, `\`, `$`, `` ` `` e a quebra de linha.
function lerAspasDuplas(texto, inicio) {
  let valor = '';
  let i = inicio;
  while (i < texto.length) {
    const c = texto[i];
    if (c === '"') return { valor, fim: i + 1 };
    if (c === '\\' && '"\\$`\n'.includes(texto[i + 1])) {
      if (texto[i + 1] !== '\n') valor += texto[i + 1];
      i += 2;
    } else {
      valor += c;
      i += 1;
    }
  }
  throw new ErroDoCurl('aspas');
}

// O texto em palavras, como o bash as separa: espaço separa; `\` no fim da linha junta as linhas;
// aspas simples guardam tudo; aspas duplas guardam quase tudo; `$'…'` traduz os escapes do C; e um
// `\` fora das aspas escapa o caractere seguinte. Palavras coladas (`-H'a: b'`) ficam uma só.
export function palavras(texto) {
  const fonte = String(texto ?? '').replace(/\r\n?/g, '\n');
  const saida = [];
  let atual = null;
  let i = 0;
  const juntar = parte => {
    atual = (atual ?? '') + parte;
  };
  while (i < fonte.length) {
    const c = fonte[i];
    if (c === '\\') {
      const seguinte = fonte[i + 1];
      if (seguinte === '\n') {
        i += 2;
      } else {
        if (seguinte !== undefined) juntar(seguinte);
        i += 2;
      }
    } else if (c === "'") {
      const fim = fonte.indexOf("'", i + 1);
      if (fim < 0) throw new ErroDoCurl('aspas');
      juntar(fonte.slice(i + 1, fim));
      i = fim + 1;
    } else if (c === '$' && fonte[i + 1] === "'") {
      const { valor, fim } = lerAnsiC(fonte, i + 2);
      juntar(valor);
      i = fim;
    } else if (c === '"') {
      const { valor, fim } = lerAspasDuplas(fonte, i + 1);
      juntar(valor);
      i = fim;
    } else if (/\s/.test(c)) {
      if (atual !== null) saida.push(atual);
      atual = null;
      i += 1;
    } else if (c === '#' && atual === null) {
      const fim = fonte.indexOf('\n', i);
      i = fim < 0 ? fonte.length : fim;
    } else {
      juntar(c);
      i += 1;
    }
  }
  if (atual !== null) saida.push(atual);
  return saida;
}

// As opções que o leitor entende, pelo nome curto e pelo longo, e o que cada uma é.
const COM_VALOR = {
  '-X': 'metodo',
  '--request': 'metodo',
  '-H': 'header',
  '--header': 'header',
  '-d': 'data',
  '--data': 'data',
  '--data-ascii': 'data',
  '--data-raw': 'raw',
  '--data-binary': 'binary',
  '--data-urlencode': 'urlencode',
  '--json': 'json',
  '-F': 'form',
  '--form': 'form',
  '--form-string': 'formString',
  '-u': 'usuario',
  '--user': 'usuario',
  '-b': 'cookie',
  '--cookie': 'cookie',
  '-m': 'tempo',
  '--max-time': 'tempo',
  '--url': 'url',
  '-A': 'agente',
  '--user-agent': 'agente',
  '-e': 'referer',
  '--referer': 'referer',
  '-x': 'proxy',
  '--proxy': 'proxy',
};
// Opções com valor que não mudam o pedido (arquivo de saída, tentativas, certificados…): o valor
// é consumido e descartado.
const VALOR_IGNORADO = new Set([
  '-o',
  '--output',
  '-w',
  '--write-out',
  '--connect-timeout',
  '--retry',
  '--retry-delay',
  '--retry-max-time',
  '-c',
  '--cookie-jar',
  '--cacert',
  '--capath',
  '--cert',
  '-E',
  '--key',
  '--resolve',
  '--limit-rate',
  '-r',
  '--range',
  '-D',
  '--dump-header',
  '-U',
  '--proxy-user',
  '--interface',
  '--dns-servers',
  '-y',
  '--speed-time',
  '-Y',
  '--speed-limit',
  '--max-redirs',
  '--trace',
  '--trace-ascii',
  '-K',
  '--config',
  '--unix-socket',
  '--noproxy',
  '-T',
  '--upload-file',
]);
const SEM_VALOR = {
  '-G': 'get',
  '--get': 'get',
  '-I': 'head',
  '--head': 'head',
  '-k': 'inseguro',
  '--insecure': 'inseguro',
};
// As letras curtas sem valor (`-sSLk` é um grupo delas).
const LETRAS_SEM_VALOR = new Set([
  'G',
  'I',
  'k',
  'L',
  's',
  'S',
  'v',
  'i',
  'f',
  'g',
  'N',
  '#',
  'q',
  '0',
  '1',
  '2',
  '3',
  '4',
  '6',
  'j',
  'l',
  'n',
  'O',
  'R',
  'Z',
  'M',
  'V',
  'h',
  'a',
  'B',
  'p',
  'J',
]);
const LETRAS_COM_VALOR = new Set(
  Object.keys(COM_VALOR)
    .concat([...VALOR_IGNORADO])
    .filter(op => /^-[A-Za-z#]$/.test(op))
    .map(op => op[1])
);

// O cmd do Windows: `^` no fim da linha ou antes de aspas (`^"`), que o "Copiar como cURL (cmd)" do
// Chrome usa. O texto não é bash e é recusado com o texto que pede o bash.
const FORMATO_CMD = /\^\s*\n|\^"|\^\\\^"/;

function aplicar(curl, tipo, valor) {
  switch (tipo) {
    case 'metodo':
      curl.metodo = valor;
      break;
    case 'header': {
      const doisPontos = valor.indexOf(':');
      if (doisPontos > 0) {
        curl.headers.push({
          nome: valor.slice(0, doisPontos).trim(),
          valor: valor.slice(doisPontos + 1).trim(),
        });
      } else if (valor.endsWith(';')) {
        // `-H 'X-Vazio;'`: o header vazio do curl.
        curl.headers.push({ nome: valor.slice(0, -1).trim(), valor: '' });
      }
      break;
    }
    case 'data':
    case 'raw':
    case 'binary':
    case 'urlencode':
      curl.dados.push({ tipo, valor });
      break;
    case 'json':
      curl.dados.push({ tipo: 'raw', valor });
      curl.headers.push(
        { nome: 'Content-Type', valor: 'application/json', de: 'json' },
        { nome: 'Accept', valor: 'application/json', de: 'json' }
      );
      break;
    case 'form':
    case 'formString': {
      const igual = valor.indexOf('=');
      if (igual > 0) {
        curl.form.push({
          nome: valor.slice(0, igual),
          valor: valor.slice(igual + 1),
          literal: tipo === 'formString',
        });
      }
      break;
    }
    case 'usuario':
      curl.usuario = valor;
      break;
    case 'cookie':
      curl.cookie = valor;
      break;
    case 'tempo':
      curl.tempoMaximo = Number(valor);
      break;
    case 'url':
      if (!curl.url) curl.url = valor;
      break;
    case 'agente':
      curl.headers.push({ nome: 'User-Agent', valor, de: 'agente' });
      break;
    case 'referer':
      curl.headers.push({ nome: 'Referer', valor, de: 'referer' });
      break;
    case 'proxy':
      if (!curl.avisos.includes('proxy')) curl.avisos.push('proxy');
      break;
    default:
  }
}

function marcar(curl, tipo) {
  if (tipo === 'get') curl.get = true;
  else if (tipo === 'head') curl.head = true;
  else if (tipo === 'inseguro' && !curl.avisos.includes('inseguro')) {
    curl.avisos.push('inseguro');
  }
}

// `{ok: true, curl}` ou `{ok: false, erro}`. O `curl`:
// - `url`: a URL como veio, com a query (sem esquema, ganha `http://`, como o curl faz);
// - `metodo` (do `-X`), `head` (`-I`) e `get` (`-G`);
// - `headers`: `[{nome, valor}]`, na ordem; os que vêm do `-A`, do `-e` e do `--json` levam `de`;
// - `dados`: `[{tipo, valor}]`, com `tipo` `data`, `raw`, `binary` ou `urlencode`;
// - `form`: os campos do `-F`, `[{nome, valor, literal}]`;
// - `usuario` (`-u`), `cookie` (`-b`) e `tempoMaximo` (`--max-time`);
// - `avisos`: `inseguro` (`-k`) e `proxy` (`-x`).
export function lerCurl(texto) {
  const fonte = String(texto ?? '').trim();
  if (FORMATO_CMD.test(fonte)) return { ok: false, erro: 'cmd' };
  let lidas;
  try {
    lidas = palavras(fonte);
  } catch (erro) {
    if (erro instanceof ErroDoCurl) return { ok: false, erro: erro.codigo };
    throw erro;
  }
  const [primeira, ...resto] = lidas;
  if (!primeira || !/(^|\/)curl(\.exe)?$/i.test(primeira)) {
    return { ok: false, erro: 'nao_e_curl' };
  }
  const curl = {
    url: null,
    metodo: null,
    head: false,
    get: false,
    headers: [],
    dados: [],
    form: [],
    usuario: null,
    cookie: null,
    tempoMaximo: null,
    avisos: [],
  };
  let i = 0;
  let soPosicionais = false;
  const proximo = () => {
    i += 1;
    return resto[i - 1];
  };
  while (i < resto.length) {
    const palavra = proximo();
    if (soPosicionais || !palavra.startsWith('-') || palavra === '-') {
      if (!curl.url) curl.url = palavra;
    } else if (palavra === '--') {
      soPosicionais = true;
    } else if (palavra.startsWith('--')) {
      if (COM_VALOR[palavra]) {
        const valor = proximo();
        if (valor !== undefined) aplicar(curl, COM_VALOR[palavra], valor);
      } else if (VALOR_IGNORADO.has(palavra)) {
        proximo();
      } else if (SEM_VALOR[palavra]) {
        marcar(curl, SEM_VALOR[palavra]);
      }
    } else {
      // Curtas: um grupo (`-sSL`), ou uma letra com o valor colado (`-XPOST`, `-d@corpo.json`).
      const letras = palavra.slice(1);
      for (let k = 0; k < letras.length; k += 1) {
        const letra = letras[k];
        const opcao = `-${letra}`;
        if (LETRAS_COM_VALOR.has(letra)) {
          const colado = letras.slice(k + 1);
          const valor = colado || proximo();
          if (valor !== undefined && COM_VALOR[opcao]) {
            aplicar(curl, COM_VALOR[opcao], valor);
          }
          break;
        }
        if (SEM_VALOR[opcao]) marcar(curl, SEM_VALOR[opcao]);
        else if (!LETRAS_SEM_VALOR.has(letra)) break;
      }
    }
  }
  if (!curl.url) return { ok: false, erro: 'sem_url' };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(curl.url)) {
    curl.url = `http://${curl.url}`;
  }
  return { ok: true, curl };
}

// A URL partida em base e query: `{base, query: [[nome, valor]]}`, com a query decodificada como o
// navegador decodifica (`URLSearchParams`). O `#` e o que vem depois saem.
export function partirUrl(url) {
  const semFragmento = String(url).split('#')[0];
  const interrogacao = semFragmento.indexOf('?');
  if (interrogacao < 0) return { base: semFragmento, query: [] };
  return {
    base: semFragmento.slice(0, interrogacao),
    query: [...new URLSearchParams(semFragmento.slice(interrogacao + 1))],
  };
}
